import * as angular from 'angular';
import 'angular-route';
import 'angular-animate';
import 'angular-sanitize';
import 'angular-local-storage';
import 'angular-loading-bar';
import 'angular-ui';
import 'angular-bootstrap-toggle';
import 'ng-showdown';
import 'oclazyload';
import { gpServerTimeHttpInterceptor } from "../app-common/util/server-time-http-interceptor";
import { ILogglyService, getScreenInformation } from "../app-common/services/jsloggly-service";
import { IAuthorisationCheckerService } from '../app-common/services/authorisation-checker-with-caching-service';
import { IRouteAuthService } from './services/route-auth-service';
import { gpCommon } from '../app-common/app';
import { gpCanUse } from '../app-common/util/capabilities';
import { isCapabilityGatingEnabled } from '../app-common/util/feature-flags';
import { AccountController_ServiceName, AccountController_Template } from './controllers/account-controller';
import { gpHome } from './app';
import boardsTemplate from './views/boards.html';
import confirmemailTemplate from './views/confirmemail.html';
import confirmemailchangeTemplate from './views/confirmemailchange.html';
import createPasswordTemplate from './views/create-password.html';
import homeTemplate from './views/home.html';
import newboardTemplate from './views/newboard.html';
import templatesTemplate from './views/templates.html';
import welcomeTemplate from './views/welcome.html';

gpHome.run(['gpRouteAuthService', function (gpRouteAuthService: IRouteAuthService) {

}]);

gpHome.run([
    '$rootScope',
    '$location',
    'gpLogglyService',
    function (
        $rootScope: ng.IRootScopeService,
        $location: ng.ILocationService,
        gpLogglyService: ILogglyService

    ) {
        $rootScope.$on("$routeChangeSuccess", ev => {
            gpLogglyService.information("Gameplan home app navigated to {Route}", [$location.path()]);
        }); 4

        gpLogglyService.information("Gameplan home app loading", []);
        gpLogglyService.information("GamePlan home app browser capabilities", [], getScreenInformation());
    }]);

// This was added to prevent url issues in router
// https://stackoverflow.com/questions/41272314/angularjs-all-slashes-in-url-changed-to-2f
gpHome.config(['$locationProvider', function ($locationProvider: ng.ILocationProvider) {
    $locationProvider.hashPrefix('');
}]);

// Regression guard. Every template this app renders is now a webpack import
// (see `template:` below, and the same across app-home/components and
// app-common/components), so nothing here is fetched over HTTP any more and this
// interceptor matches nothing today.
//
// It stays because the failure it prevents is silent and expensive. A templateUrl
// points at a URL that never changes between releases, so a release that alters
// the template leaves a browser holding the old copy rendering new CSS and JS
// against old markup — which is what broke the plans list. Web.config's
// Cache-Control: no-cache does not save you: it only binds browsers that receive
// it, and a copy cached before that policy existed is held under heuristic
// freshness and never revalidated. Changing the URL is the only thing that breaks
// an already-poisoned cache, since the stored entry can never match the new key.
//
// So if a templateUrl reappears here, it is versioned from the first release
// rather than after someone reports a broken page. Mirrors app-workspace/app.ts.
gpHome.config([
    '$httpProvider',
    function ($httpProvider: ng.IHttpProvider) {
        $httpProvider.interceptors.push(() => {
            return ({
                'request': function (config: ng.IRequestConfig) {
                    if (__gameplan_version && config.url.endsWith('.html') && config.url.startsWith('/app-')) {
                        config.url = config.url + "?version=" + __gameplan_version;
                    }

                    return config;
                },
            });
        });

        // Cross-checks the page shell's server time against a header that
        // cannot be stale, and keeps the clock fed on pages that never open a
        // SignalR connection (#763).
        $httpProvider.interceptors.push(() => gpServerTimeHttpInterceptor());
    }
]);

gpHome.config(["$routeProvider", function ($routeProvider: ng.route.IRouteProvider) {


    if (window.location.pathname === '/logout') {
        // If we are logging out, then we don't want to provide access to any other route at all.
        $routeProvider.otherwise({

            controller: "indexController",
            template: homeTemplate
        });

        return;
    }


    $routeProvider.when("/home", {
        redirectTo: "/",
    });

    $routeProvider.when("/login", {
        template: "<gp-login></gp-login>"
    });

    $routeProvider.when("/signup", {
        template: "<gp-signup></gp-signup>"
    });

    $routeProvider.when("/confirmemail/:userId/:code", {
        controller: "confirmemailController",
        template: confirmemailTemplate
    });

    $routeProvider.when("/confirmemailchange/:userId/:code", {
        controller: "confirmemailchangeController",
        template: confirmemailchangeTemplate
    });

    $routeProvider.when("/account", {
        controller: AccountController_ServiceName,
        template: AccountController_Template,
    });

    $routeProvider.when("/welcome", {
        controller: "gpWelcomeController",
        template: welcomeTemplate
    });

    $routeProvider.when("/plans", {
        controller: "boardsController",
        template: boardsTemplate
    });

    $routeProvider.when("/plans/new", {
        controller: "newboardController",
        template: newboardTemplate
    });

    // #566: the Templates page, not only the nav link that reaches it. Hiding a
    // link removes the route nobody types; a bookmark, a shared URL or the back
    // button all still land here, and the page would then offer the whole
    // capability to a licence that does not carry it.
    //
    // `resolve` rather than a check inside the controller: a rejected resolve
    // stops the route before the controller runs, so nothing of the page is
    // constructed and no template request goes out.
    $routeProvider.when("/templates", {
        controller: "templatesController",
        template: templatesTemplate,
        resolve: {
            templatesAllowed: ['$q', '$location', function ($q: ng.IQService, $location: ng.ILocationService) {
                if (!isCapabilityGatingEnabled() || gpCanUse('templates').enabled) {
                    return true;
                }

                $location.path('/plans');
                return $q.reject('templates-withheld');
            }],
        },
    });

    $routeProvider.when("/analyze", {
        template: "<gp-analyze-list></gp-analyze-list>"
    });

    $routeProvider.when("/analyze/:analysisProjectId", {
        template: "<gp-analyze-detail></gp-analyze-detail>"
    });

    $routeProvider.when("/create-password/:userId/:email/:code", {
        controller: "createPasswordController",
        template: createPasswordTemplate
    });

    $routeProvider.when("/password/:userId/:code/:reset", {
        controller: "createPasswordController",
        template: createPasswordTemplate
    });

    $routeProvider.otherwise({

        controller: "indexController",
        template: homeTemplate
    });
}]);

gpHome.run(['authService', function (authService: IAuthorisationCheckerService) {
    authService.checkAuthorisationIfNeeded();
}]);

gpHome.run(['$rootScope', '$location', '$window', function ($rootScope: ng.IRootScopeService, $location: ng.ILocationService, $window: ng.IWindowService) {

    let tagErrorPrinted = false;
    function printGoogleTagErrorIfNeeded() {
        if (tagErrorPrinted) return;

        if (typeof gtag === 'undefined') {
            tagErrorPrinted = true;
            console.log("**** Google tag manager not loaded properly ****")
        }
    }

    $window.dataLayer = $window.dataLayer || [];

    $rootScope.$on('$routeChangeSuccess', function () {
        printGoogleTagErrorIfNeeded();

        if (typeof gtag !== 'undefined' && gtag) {
            gtag('config', __gameplan_google_analytics_id__, {
                'page_title': 'app' + $location.path(),
                'page_path': 'app' + $location.path(),
            });
        }

        if ($window.dataLayer) {
            $window.dataLayer.push({
                event: 'ngRouteChange',
                attributes: {
                    route: $location.path()
                }
            });
        }

    });

}]);
